/**
 * Kasa Dashboard Page
 *
 * Unified view of all smart devices with real-time status updates.
 * Devices are fetched from all user's orgs for a complete picture.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useKasaStore } from './kasa-store';
import { CredentialsSetupCard } from './components/CredentialsSetupCard';
import { DeviceCard } from './components/DeviceCard';
import { EnergyMonitorCard } from './components/EnergyMonitorCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plug, AlertCircle, Search } from 'lucide-react';

interface KasaDashboardPageProps {
  embedded?: boolean;
}

const KasaDashboardPage: React.FC<KasaDashboardPageProps> = ({ embedded = false }) => {
  const {
    hasCredentials,
    devices,
    esp32Devices,
    energy,
    isLoading,
    isDiscovering,
    error,
    fetchSettings,
    fetchDevices,
    fetchESP32Devices,
    discoverDevices,
    fetchEnergy,
    clearError,
  } = useKasaStore();

  const [initialized, setInitialized] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const init = async () => {
      await fetchSettings();
      setInitialized(true);
    };
    init();
  }, [fetchSettings]);

  // Fetch devices on init and poll every 5 seconds for status updates
  useEffect(() => {
    if (initialized) {
      fetchDevices();
      fetchESP32Devices();
      pollRef.current = setInterval(() => {
        fetchDevices();
        fetchESP32Devices();
      }, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [initialized, fetchDevices, fetchESP32Devices]);

  // Fetch energy for supported devices
  useEffect(() => {
    for (const device of devices) {
      if (device.supportsEmeter) {
        fetchEnergy(device.host);
      }
    }
  }, [devices, fetchEnergy]);

  const handleRefresh = () => {
    discoverDevices();
  };

  // Merge direct and ESP32-discovered devices, preferring ESP32 entries (correct control path)
  // Dedup by both deviceId and host IP to avoid showing the same physical device twice
  const allDevices = React.useMemo(() => {
    if (esp32Devices.length > 0) {
      const esp32Ids = new Set(esp32Devices.map((d) => d.deviceId));
      const esp32Hosts = new Set(esp32Devices.map((d) => d.host).filter(Boolean));
      const directOnly = devices.filter(
        (d) => !esp32Ids.has(d.deviceId) && !esp32Hosts.has(d.host)
      );
      return [...esp32Devices, ...directOnly];
    }
    return devices;
  }, [devices, esp32Devices]);

  const onlineCount = allDevices.filter((d) => d.status !== 'unreachable').length;
  const energyReadings = Object.values(energy);

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 max-w-6xl mx-auto space-y-6'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Plug className={`${embedded ? 'h-6 w-6' : 'h-8 w-8'} shrink-0 text-primary`} />
          <div className="min-w-0">
            {embedded ? (
              <h2 className="text-xl font-semibold truncate">Connected Devices</h2>
            ) : (
              <h1 className="text-2xl font-bold tracking-tight truncate">Connected Devices</h1>
            )}
            <p className="text-sm text-muted-foreground truncate">
              Link and manage TP-Link Kasa, Tapo, and other smart device integrations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {allDevices.length > 0 && (
            <Badge variant="outline" className="text-xs">
              {onlineCount}/{allDevices.length} online
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isDiscovering || isLoading}>
            {isDiscovering ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="ml-1">Discover</span>
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={clearError} className="ml-auto">
            Dismiss
          </Button>
        </div>
      )}

      {/* Credentials Setup */}
      {!hasCredentials && <CredentialsSetupCard />}

      {/* Energy Overview */}
      {energyReadings.length > 0 && <EnergyMonitorCard readings={energyReadings} />}

      {/* Unified Devices Grid */}
      {allDevices.length === 0 && !isLoading && !isDiscovering && (
        <div className="text-center py-12 text-muted-foreground">
          <Plug className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No devices found on the network.</p>
          <p className="text-xs mt-1">Click "Discover" to scan for Kasa/Tapo devices.</p>
        </div>
      )}

      {allDevices.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {allDevices.map((device) => (
            <DeviceCard
              key={`${device.deviceId}-${device.childId || ''}`}
              device={device}
              energy={energy[device.deviceId]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default KasaDashboardPage;
